import React, { useState } from "react";
import {
  Box,
  Avatar,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Stack,
  Badge,
  Grid,
  Button,
  IconButton,
  Tabs,
  Tab,
  Container,
} from "@mui/material";
import {
  EmailOutlined,
  PhoneOutlined,
  PublicOutlined,
  LocationOnOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import CircleIcon from "@mui/icons-material/Circle";
import InteractiveAction2 from "../../../../widgets/InteractiveAction2";
import { MySelectedProductCats } from "./MySelectedProductCats";
import ProductsList from "./ProductsList";
import TeamMembersList from "../TeamMembersList";
import BrochuresList from "./BrochuresList";
import useGetEventSettings from "../../../hooks/useGetEventSettings";
import { getUrlWithProtocol } from "../../../../utility/utility";
import parse from "html-react-parser";
import xLogo from "../../../../assets/x_logo.png";

const SectionCard = ({ title, children }) => (
  <Card
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: "1px solid #e2e8f0",
      backgroundColor: "#fff",
      mb: 3,
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Card>
);

const ExhibitorDetail = ({ response }) => {
  const { data: eD } = useGetEventSettings();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const isOnline = false;

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Left Column: Profile Card & Info */}
          <Grid item xs={12} md={4} lg={3}>
            {/* Main Profile Info Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  py: 4,
                }}
              >
                <Box sx={{ position: "relative", mb: 2 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                      isOnline ? (
                        <CircleIcon
                          sx={{
                            color: "#22c55e",
                            fontSize: 16,
                            border: "2px solid #fff",
                            borderRadius: "50%",
                          }}
                        />
                      ) : null
                    }
                  >
                    <Avatar
                      src={response.profile_image}
                      alt={response.companyName?.value}
                      sx={{
                        width: 120,
                        height: 120,
                        border: "4px solid white",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                        fontSize: 40,
                        fontWeight: 800,
                        color: "#0288d1",
                        backgroundColor: "#f1f5f9",
                      }}
                    >
                      {response.companyName?.value?.charAt(0)}
                    </Avatar>
                  </Badge>
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color="primary.main"
                  gutterBottom
                >
                  {response.companyName?.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#64748b", mb: 2 }}
                >
                  {`${response.contactSalutation?.value || ""} ${
                    response.contactFirstName?.value || ""
                  } ${response.contactLastName?.value || ""}`}
                  {response.contactDesignation?.value
                    ? ` • ${response.contactDesignation.value}`
                    : ""}
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <InteractiveAction2 item={response} showDetail={false} />
                </Box>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocationOnOutlined
                    sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                  />
                  <Typography
                    component={Link}
                    to={`/hall-view/${response.hallNumber?.value}?un=${response.username}`}
                    variant="body2"
                    fontWeight={600}
                    color="primary"
                    sx={{
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Hall {response.hallNumber?.value} / Stand{" "}
                    {response.standNumber?.value}
                  </Typography>
                </Box>

                {response.mobile?.value && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PhoneOutlined
                      sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                    />
                    <Typography
                      component="a"
                      href={`tel:${response.mobile.value}`}
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {response.mobile.value}
                    </Typography>
                  </Box>
                )}

                {response.contactEmail?.value && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EmailOutlined
                      sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                    />
                    <Typography
                      component="a"
                      href={`mailto:${response.contactEmail.value}`}
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                          wordBreak: "break-all",
                        },
                      }}
                    >
                      {response.contactEmail.value}
                    </Typography>
                  </Box>
                )}

                {response.website?.value && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <PublicOutlined
                      sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                    />
                    <Typography
                      component="a"
                      href={getUrlWithProtocol(response.website.value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{
                        textDecoration: "none",
                        wordBreak: "break-all",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {response.website.value}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Social Media
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      response?.socialLinks?.value?.facebook
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#1877F2",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <FacebookIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      response?.socialLinks?.value?.instagram
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#E1306C",
                      "&:hover": { backgroundColor: "#fce4ec" },
                    }}
                  >
                    <InstagramIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      response?.socialLinks?.value?.linkedin
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#0A66C2",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <LinkedInIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      response?.socialLinks?.value?.twitter
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                    }}
                  >
                    <img
                      src={xLogo}
                      alt="x"
                      style={{ height: "14px", width: "14px" }}
                    />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>

            {/* Video Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Company Video
                  </Typography>
                </Box>

                {response.companyVideo ? (
                  <Box
                    className="responsive-content"
                    sx={{ borderRadius: 2, overflow: "hidden" }}
                  >
                    {parse(response.companyVideo)}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: "center",
                      backgroundColor: "#f8fafc",
                      borderRadius: 2,
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <PlayCircleOutlineIcon
                      sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      No Video Yet
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Banner & Tabs Content */}
          <Grid item xs={12} md={8} lg={9}>
            {/* Banner Section */}
            <Box
              sx={{
                position: "relative",
                height: { xs: 120, sm: 160, md: 220 },
                backgroundImage: `url(${
                  response.profile_banner || eD?.defProfileBanner
                })`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "16px 16px 0 0",
                boxShadow: "0px -4px 20px rgba(0,0,0,0.05)",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "30%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.3), transparent)",
                },
              }}
            />

            {/* Tabs Navigation */}
            <Card
              elevation={0}
              sx={{
                borderRadius: "0 0 16px 16px",
                border: "1px solid #eef2f6",
                borderTop: "none",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
                mb: 3,
              }}
            >
              <Tabs
                value={currentTab}
                variant="scrollable"
                scrollButtons="auto"
                onChange={handleTabChange}
                TabIndicatorProps={{
                  style: { backgroundColor: "#f4741f", height: 3 },
                }}
                sx={{
                  px: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minWidth: 120,
                    py: 2.5,
                  },
                  "& .Mui-selected": {
                    color: "#f4741f !important",
                  },
                }}
              >
                <Tab label="About" />
                <Tab label="Products" />
                <Tab label="Team Members" />
                <Tab label="Brochures" />
              </Tabs>
            </Card>

            {/* Tab Content Area */}
            <Box sx={{ mt: 3 }}>
              {currentTab === 0 && (
                <Stack direction="column" spacing={3}>
                  {/* NEW: Company About Section */}
                  {response.about?.value && (
                    <SectionCard title={response.about.label || "About"}>
                      <Box
                        sx={{
                          color: "#475569",
                          lineHeight: 1.7,
                          "& p": { mt: 0, mb: 2 },
                          "& h2, & h3, & h4": {
                            color: "#1e293b",
                            mt: 0,
                            mb: 1.5,
                          },
                        }}
                      >
                        {parse(response.about.value)}
                      </Box>
                    </SectionCard>
                  )}

                  <SectionCard title="Address">
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#475569",
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {[
                        [response.address1?.value, response.address2?.value]
                          .filter(Boolean)
                          .join(", "),
                        response.city?.value?.name,
                        response.state?.value?.name,
                        response.country?.value?.name,
                        response.pin?.value && `PIN: ${response.pin.value}`,
                      ]
                        .filter(Boolean)
                        .join("\n")}
                    </Typography>
                  </SectionCard>

                  <SectionCard title="Industry Sectors">
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {(response.industrySector?.value || []).map(
                        (sector, index) => (
                          <Chip
                            key={index}
                            label={sector}
                            sx={{
                              backgroundColor: "#f1f5f9",
                              color: "#334155",
                              fontWeight: 600,
                              borderRadius: "6px",
                            }}
                          />
                        )
                      )}
                    </Stack>
                  </SectionCard>

                  <SectionCard title="Product Categories">
                    <MySelectedProductCats
                      items={response.productCategories?.value || []}
                    />
                  </SectionCard>
                </Stack>
              )}
              {currentTab === 1 && (
                <SectionCard title="Products">
                  <ProductsList user={response} />
                </SectionCard>
              )}
              {currentTab === 2 && (
                <SectionCard title="Team Members">
                  <TeamMembersList user={response} />
                </SectionCard>
              )}
              {currentTab === 3 && (
                <SectionCard title="Brochures">
                  <BrochuresList user={response} />
                </SectionCard>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ExhibitorDetail;
